<template>
    <div class="marquee-text-root" ref="root">
        <div v-if="isPreRender" key="hideContent"><!-- 预渲染空节点 --></div>
        <div
            v-else
            ref="content"
            :class="[
                'marquee-text-content',
                {
                    horizontal: scrollDirect === SCROLL_DIRECT.HORIZONTAL,
                    vertical: scrollDirect === SCROLL_DIRECT.VERTICAL,
                    continuous: scrollType === SCROLL_TYPE.CONTINUOUS,
                    page: scrollType === SCROLL_TYPE.PAGE,
                    seamless: isSeamless
                }
            ]"
            :style="{
                '--scroll-time': `${scrollTime}s`,
                '--page-time': `${pageTime}s`,
                '--content-width': cssContentWidth + 'px',
                '--content-height': cssContentHeight + 'px',
                '--transform-x': cssTranslateX + 'px',
                '--transform-y': cssTranslateY + 'px'
            }"
        >
            <div
                class="item"
                ref="contentItem"
                :key="index"
                :style="{
                    '--content-margin': cssContentMargin + 'px'
                }"
                v-for="(item, index) in contentList"
            >
                <div class="content-wrapper" v-html="item.content"></div>
            </div>
        </div>
    </div>
</template>

<script>
import { SCROLL_TYPE, SCROLL_DIRECT } from '../constants/enum';
import FEGORender from '@futuweb/fego-render';
import FEGOBridge from '@futuweb/fego-bridge';

const { throttle } = FEGOBridge;
const {
    common: {
        env: { isPreRender }
    }
} = FEGORender;

export default {
    name: 'marque-text',
    props: {
        tpl: Object,
        scrollType: String,
        scrollDirect: String,
        contentMargin: String,
        scrollTime: Number,
        pageTime: Number,
        isSeamless: Boolean,
        contentList: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            SCROLL_TYPE,
            SCROLL_DIRECT,
            pageTimer: null,
            pageListener: null, // 监听器
            pageCount: 1, // 翻页滚动时处于第几页
            resizeListener: null,
            isPreRender,
            cssContentMargin: this.$props.contentMargin,
            cssContentWidth: 0,
            cssContentHeight: 0,
            cssTranslateX: 0,
            cssTranslateY: 0
        };
    },
    mounted() {
        if (isPreRender) {
            return;
        }
        this.show = true;
        const fn = () => {
            this.cssContentMargin = this.calcContentMargin(this.contentMargin);
            this.setContentSize();
            if (this.scrollType === SCROLL_TYPE.PAGE) {
                this.clearPageAnime();
                this.executePage();
            }
        };
        fn();
        // 容器与字体尺寸为rem，内部内容为 px 计算，尺寸变化时，需要监听 resize 重新计算
        this.resizeListener = throttle(fn, 100);
        window.addEventListener('resize', this.resizeListener);
    },
    beforeDestroy() {
        this.clearPageAnime();
        window.removeEventListener('resize', this.resizeListener);
    },
    methods: {
        setContentSize() {
            const content = this.$refs.content;
            // pc 与 h5 切换销毁时判断节点是否存在
            if (content) {
                this.removeCloneContent();
                if (this.scrollDirect === SCROLL_DIRECT.HORIZONTAL) {
                    this.$refs.content.style.height = '100%';
                    this.setContentWidth();
                    this.setTranslateX();
                } else {
                    this.$refs.content.style.width = '100%';
                    this.setContentHeight();
                    this.setTranslateY();
                }
                this.resetAnime();
            }
            // 无缝滚动下，添加复制节点
            this.isSeamless && this.addCloneContent();
        },
        // render 下计算内容间距
        // 相对 375px 进行计算，上限为二倍图的间距大小 (750px)
        calcContentMargin(px) {
            let ret = px;
            const calcPx = (window.innerWidth / 375) * px;
            if (calcPx <= px * 2) {
                ret = calcPx;
            }
            return ret;
        },
        // 计算内容的总宽度, 只存在连续滚动
        // 横向无缝滚动要 - 1 个内容间距
        setContentWidth() {
            this.$nextTick(() => {
                let width = 0;
                const contentList = this.$refs.contentItem;
                const contentListLength = contentList && contentList.length;
                width += (contentListLength + 1) * this.calcContentMargin(this.contentMargin);
                if (this.isSeamless) {
                    width -= this.calcContentMargin(this.contentMargin);
                }
                for (let i = 0; i < contentListLength; i++) {
                    const item = this.$refs.contentItem[i];
                    const { width: itemWidth } = item.getBoundingClientRect();
                    width += itemWidth;
                }
                this.cssContentWidth = width;
                this.$refs.content.style.width = width + 'px';
            });
        },
        setTranslateX() {
            this.$nextTick(() => {
                const root = this.$refs.root;
                const { width } = root.getBoundingClientRect();
                this.cssTranslateX = width;
            });
        },
        // 计算内容的总高度，分连续滚动和翻页
        // 纵向无缝滚动要 +1 个内容间距
        setContentHeight() {
            this.$nextTick(() => {
                let height = 0;
                if (this.scrollType === SCROLL_TYPE.CONTINUOUS) {
                    const contentList = this.$refs.contentItem;
                    const contentListLength = contentList && contentList.length;
                    height += (contentListLength - 1) * this.calcContentMargin(this.contentMargin);
                    if (this.isSeamless) {
                        height += this.calcContentMargin(this.contentMargin);
                    }
                    for (let i = 0; i < contentListLength; i++) {
                        const item = this.$refs.contentItem[i];
                        item.style.height = 'unset'; // 去除翻页设置的 height
                        const { height: itemHeight } = item.getBoundingClientRect();
                        height += itemHeight;
                    }
                } else {
                    // 翻页下，内容容器高度等于根元素的高度 * 内容个数。
                    const { height: rootHeight } = this.$refs.root.getBoundingClientRect();
                    height += rootHeight * this.contentList.length;
                }
                this.cssContentHeight = height;
                this.$refs.content.style.height = height + 'px';
                // 给内容元素设高度
                if (this.scrollType === SCROLL_TYPE.PAGE) {
                    for (let i = 0; i < this.$refs.contentItem.length; i++) {
                        const item = this.$refs.contentItem[i];
                        item.style.height = height / this.$refs.contentItem.length + 'px';
                    }
                }
            });
        },
        setTranslateY() {
            this.$nextTick(() => {
                const root = this.$refs.root;
                const { height } = root.getBoundingClientRect();
                this.cssTranslateY = height;
            });
        },
        // 无缝滚动添加克隆节点
        addCloneContent() {
            this.$nextTick(() => {
                const contentNode = this.$refs.content;
                if (contentNode) {
                    const contentList = contentNode.querySelectorAll('.item');
                    const contentCount = contentList.length;
                    for (let i = 0; i < contentCount; i++) {
                        const cloneContent = contentList[i].cloneNode(true);
                        cloneContent.classList.add('clone-content');
                        contentNode.appendChild(cloneContent);
                    }
                }
            });
        },
        // 删除克隆出来的节点
        removeCloneContent() {
            const contentNode = this.$refs.content;
            if (contentNode) {
                const cloneList = contentNode.querySelectorAll('.clone-content');
                for (let i = cloneList.length - 1; i >= 0; i--) {
                    contentNode.removeChild(cloneList[i]);
                }
            }
        },
        // 翻页时，动画逻辑与定时器设置
        executePage() {
            this.pageTimer = setInterval(() => {
                // 涉及 dom 操作，放入下一个 tick 执行
                this.$nextTick(() => {
                    const contentEle = this.$refs.content;
                    const contentEleHeight = parseFloat(contentEle && contentEle.style.height);
                    const contentItemHeight = contentEleHeight / this.contentList.length;
                    if (contentEleHeight && contentItemHeight) {
                        this.$refs.content.style.transition = 'all 300ms linear';
                        this.$refs.content.style.transform = `translateY(-${this.pageCount * contentItemHeight}px)`;
                        this.pageCount++; // 本次到第几页
                    }
                });
            }, this.pageTime * 1000);
            this.pageListener = () => {
                // 翻页到复制节点时，返回到第一页
                if (this.pageCount >= this.contentList.length + 1) {
                    this.$nextTick(() => {
                        // 执行重置逻辑
                        this.pageCount = 1;
                        this.$refs.content.style.transition = 'unset';
                        this.$refs.content.style.transform = `translateY(0)`;
                    });
                }
            };
            this.$refs.content && this.$refs.content.addEventListener('transitionend', this.pageListener);
        },
        // 删除翻页动画定时器与监听器.
        clearPageAnime() {
            this.pageCount = 1;
            this.$nextTick(() => {
                const content = this.$refs.content;
                if (content) {
                    this.$refs.content.style.transition = 'unset';
                    this.$refs.content.style.transform = `translateY(0)`;
                }
            });
            if (this.pageTimer) {
                clearInterval(this.pageTimer);
                this.pageTimer = null;
            }
            if (this.pageListener && this.$refs.content) {
                this.$refs.content.removeEventListener('transitionend', this.pageListener);
            }
        },
        // @keyframes 使用了 css 变量
        // 在低版本 safari 存在无法正确读取 css 变量的情况
        // 针对这种情况需要 reset 动画
        resetAnime() {
            this.$nextTick(() => {
                const content = this.$refs.content;
                if (content) {
                    const contentStyle = getComputedStyle(content);
                    const saveAnimation = contentStyle.animation;
                    content.style.animation = 'unset';
                    this.$nextTick(() => {
                        content.style.animation = saveAnimation;
                    });
                }
            });
        }
    }
};
</script>

<style lang="less" scoped>
@keyframes horizontalMarqueeAnim {
    0% {
        transform: translateX(var(--transform-x));
    }
    100% {
        transform: translateX(-100%);
    }
}

@keyframes verticalMarqueeAnim {
    0% {
        transform: translateY(var(--transform-y));
    }
    100% {
        transform: translateY(-100%);
    }
}

@keyframes horizontalSeamlessMarqueeAnim {
    0% {
        left: 0;
    }
    100% {
        transform: translateX(-100%);
    }
}

@keyframes verticalSeamlessMarqueeAnim {
    0% {
        top: 0;
    }
    100% {
        transform: translateY(-100%);
    }
}

.marquee-text-root {
    overflow: hidden !important;
    position: relative !important;
    .marquee-text-content {
        position: absolute;
        display: flex;
        align-items: center;
        height: 100%;
        transform-style: preserve-3d;
        // 滚动方向水平，且连续滚动
        &.horizontal.continuous {
            animation: horizontalMarqueeAnim var(--scroll-time) linear 0s infinite;
            animation-fill-mode: forwards;
            &.seamless {
                animation-name: horizontalSeamlessMarqueeAnim;
            }
            .item {
                white-space: nowrap;
                margin-left: var(--content-margin);
                flex-shrink: 0;
                min-width: 0;
                &:last-child {
                    margin-right: var(--content-margin);
                }
            }
        }
        // 滚动方向纵向，且连续滚动
        &.vertical.continuous {
            flex-direction: column;
            animation: verticalMarqueeAnim var(--scroll-time) linear 0s infinite;
            animation-fill-mode: forwards;
            &.seamless {
                animation-name: verticalSeamlessMarqueeAnim;
            }
            .item {
                white-space: nowrap;
                margin-bottom: var(--content-margin);
                flex-shrink: 0;
                min-height: 0;
                &:last-child {
                    margin-bottom: 0;
                }
            }
        }
        // 纵向翻页
        &.vertical.page {
            flex-direction: column;
            top: 0;
            transition: all 300ms linear; // 过渡时长
            .item {
                white-space: nowrap;
                flex-shrink: 0;
                display: flex;
                align-items: center;
            }
        }
    }
}
</style>
